import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

const STORAGE_KEY = 'wiki_import_basket_v1';

/**
 * Rows queued from Wiki catalog for bulk import into {@code local_assets}.
 * Persisted in sessionStorage so the basket survives refresh within the same tab.
 */
export const useWikiImportBasketStore = defineStore('wikiImportBasket', () => {
    const items = ref([]);

    function loadFromStorage() {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                items.value = Array.isArray(parsed) ? parsed : [];
            }
        } catch {
            items.value = [];
        }
    }

    function persist() {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items.value));
        } catch {
            /* ignore quota */
        }
    }

    /**
     * @param {object} mapped - Output of {@code mapApiItem} in WikiComponent (includes ids + display fields)
     * @returns {boolean} true if added, false if duplicate
     */
    function addFromWikiItem(mapped) {
        const wikiId = mapped.id;
        if (!wikiId || items.value.some((i) => i.wikiId === wikiId)) {
            return false;
        }
        const defaultCategoryId =
            mapped.suggested_category_id != null && mapped.suggested_category_id !== ''
                ? Number(mapped.suggested_category_id)
                : null;

        items.value.push({
            wikiId,
            master_asset_id: mapped.master_asset_id,
            sub_asset_id: mapped.sub_asset_id,
            asset_type: mapped.asset_type || null,
            catalog_name: mapped.name,
            custom_name: '',
            category_id: Number.isFinite(defaultCategoryId) ? defaultCategoryId : null,
            rate_type: 'daily',
            rate: null,
            replacement_value: null,
            stock_type: 'serial',
            origin: 'catalog',
            notes: '',
            is_active: true,
        });
        persist();

        return true;
    }

    function remove(wikiId) {
        items.value = items.value.filter((i) => i.wikiId !== wikiId);
        persist();
    }

    function clear() {
        items.value = [];
        persist();
    }

    const count = computed(() => items.value.length);

    return {
        items,
        count,
        addFromWikiItem,
        remove,
        clear,
        loadFromStorage,
    };
});
